/**
 * Which passes this page watched run and land — the one rule both per-browser
 * records are kept by: the seconds a pass took (`core/passTime.ts`) and the
 * tokens it drew (`core/passDraw.ts`, D311).
 *
 * Its own module, and not the seconds' — because the seconds are read by the
 * page and nothing else (invariant 6, pinned by `passTime.test.ts`), while the
 * draws are also read by the figures a run is quoted at. A watcher living in
 * the seconds' module would put them one import away from the engine.
 *
 * "Landed" is the Announcer's rule (`ui/Announcer.tsx`): an op seen unfinished
 * and now finished. An op that arrives already finished — a restored
 * exploration, a shared copy, an undo — was not run here, and adds nothing.
 */

import type { Op, Session } from './types'

/** The slice of the store the watcher reads — typed here so a test can hand it
 *  a fake, and so this module needs no runtime import of the store. */
export interface OpsStore {
  getState: () => { session: Session }
  subscribe: (fn: (s: { session: Session }, prev: { session: Session }) => void) => () => void
}

/**
 * Call `landed` for every pass this page watched run and land. One rule shared
 * by both records, so a pass one counts is a pass the other does. Installed once
 * at boot, outside React.
 */
export function watchLandings(store: OpsStore, landed: (op: Op, session: Session) => void) {
  const running = new Set<string>()
  const baseline = (session: Session) => {
    running.clear()
    for (const o of session.ops) if (o.finishedAt === undefined) running.add(o.id)
  }
  let sessionId = store.getState().session.id
  baseline(store.getState().session)
  return store.subscribe((s, prev) => {
    if (s.session.ops === prev.session.ops && s.session.id === prev.session.id) return
    // Another document, or the worked example reminted under its own id by the
    // pass now starting: whatever it holds unfinished is watched from here.
    if (s.session.id !== sessionId) {
      sessionId = s.session.id
      baseline(s.session)
      return
    }
    for (const o of s.session.ops) {
      if (o.finishedAt === undefined) running.add(o.id)
      else if (running.delete(o.id)) landed(o, s.session)
    }
  })
}
